import { useEffect, useMemo, useState } from 'react'
import { useMatch, useNavigate, useParams } from 'react-router-dom'
import { useIsMobile, useMobileDetailRouteFallback, useShowDetailsPane } from '../../hooks'
import { useGetGoalReview } from '../../services/api/goals.hooks'
import Log from '../../services/api/log'
import { icons } from '../../styles/images'
import { DAILY_HOME_AUTOMATIC_REDIRECT_STATE } from '../../utils/dailyHome'
import Spinner from '../atoms/Spinner'
import GTButton from '../atoms/buttons/GTButton'
import { useCalendarContext } from '../calendar/CalendarContext'
import EmptyDetails from '../details/EmptyDetails'
import GoalDetails from '../goals/GoalDetails'
import GoalReviewPane from '../goals/GoalReviewPane'
import GoalsList from '../goals/GoalsList'
import { useGoalCreation } from '../goals/creation/shared/GoalCreationContext'
import {
    isReviewable,
    nextUnreadGoalId,
    reviewEntryLabel,
    reviewEntrySummary,
    reviewQueue,
} from '../goals/goalReviewRules'
import { archivedGoals } from '../goals/goalTypes'
import { Header } from '../molecules/Header'
import ScrollableListTemplate from '../templates/ScrollableListTemplate'

const GoalsView = () => {
    const { allGoals, areGoalsLoading, openFlow, iteration } = useGoalCreation()
    const { goalId } = useParams()
    // /goals/review and /goals/review/:goalId: the weekly review, with the cards as its queue and
    // the details pane holding each step.
    const isReview = !!useMatch('/goals/review/*')
    const navigate = useNavigate()
    const isMobile = useIsMobile()
    const selectedGoalId = goalId ?? null
    const selectedGoal = allGoals.find((goal) => goal.id === selectedGoalId)
    const { setCalendarType } = useCalendarContext()
    const showDetailsPane = useShowDetailsPane()
    const { data: review, isLoading: isReviewLoading, isError: isReviewUnavailable } = useGetGoalReview()
    const [showArchived, setShowArchived] = useState(false)
    // The toggle appears only once there is something behind it, so an account that has never
    // archived a goal never sees a filter for an empty set.
    const archivedCount = archivedGoals(allGoals).length
    const shouldShowMobileDetailSpinner = useMobileDetailRouteFallback({
        isMobile,
        // The review resolves its own route below; "review" must not be validated as a goal id.
        detailId: isReview ? undefined : goalId,
        canValidate: !areGoalsLoading,
        hasSelectedDetail: Boolean(selectedGoal),
        listPath: '/goals',
    })

    const isReviewReady = isReview && !areGoalsLoading && !isReviewLoading
    const hasQueue = reviewQueue(allGoals, review).length > 0
    const nextUnread = nextUnreadGoalId(allGoals, review)

    // Where a review URL lands once the goals and the week are known. Nothing to review (or the week
    // could not be loaded) goes back to the plain Goals page and its empty state; a bare
    // /goals/review opens the next unread goal, and stays put to show the summary when none is left;
    // an id that is not a goal falls back to that same choice.
    useEffect(() => {
        if (!isReviewReady) return
        if (!review || isReviewUnavailable || !hasQueue)
            return navigate('/goals', { replace: true, state: DAILY_HOME_AUTOMATIC_REDIRECT_STATE })
        if (goalId && !selectedGoal) return navigate('/goals/review', { replace: true })
        if (!goalId && nextUnread) navigate(`/goals/review/${nextUnread}`, { replace: true })
    }, [isReviewReady, review, isReviewUnavailable, hasQueue, goalId, selectedGoal, nextUnread, navigate])

    // The review happens in the details pane, which week view hides below the ultrawide width.
    useEffect(() => {
        if (isReview && !showDetailsPane) setCalendarType('day')
    }, [isReview, showDetailsPane, setCalendarType])

    useEffect(() => {
        if (isReview) Log('goal_review_opened')
    }, [isReview])

    // During a review each answered card says what was said; paused cards say why they are passed over.
    const reviewLines = useMemo(() => {
        if (!isReview || !review) return undefined
        const lines: Record<string, string> = {}
        allGoals.forEach((goal) => {
            if (goal.status === 'paused') lines[goal.id] = 'Paused goals are not walked in a review.'
        })
        review.entries.forEach((entry) => (lines[entry.goalId] = reviewEntrySummary(entry)))
        return lines
    }, [isReview, review, allGoals])

    const toggleSelection = (id: string) => {
        const goal = allGoals.find((candidate) => candidate.id === id)
        // In a review a card is a place in the queue, so clicking one jumps to it; a goal the review
        // does not walk opens as it normally would, which leaves the review with its answers kept.
        if (isReview && goal && isReviewable(goal)) return navigate(`/goals/review/${id}`)
        navigate(selectedGoalId === id && !isReview ? '/goals' : `/goals/${id}`)
    }

    const reviewLabel = reviewEntryLabel(allGoals, review)

    return (
        <>
            <ScrollableListTemplate>
                <Header folderName="Goals" />
                {allGoals.length > 0 && iteration !== 'mirror' && (
                    <div className="mb-3 flex items-center gap-2">
                        {isReview ? (
                            <span className="px-2 text-body-sm text-muted-foreground">Weekly review in progress</span>
                        ) : (
                            reviewLabel && (
                                <button
                                    type="button"
                                    onClick={() => navigate('/goals/review')}
                                    className="rounded-md px-2 py-1 text-body-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                >
                                    {reviewLabel}
                                </button>
                            )
                        )}
                        <span className="ml-auto" />
                        {archivedCount > 0 && (
                            <button
                                type="button"
                                aria-pressed={showArchived}
                                onClick={() => setShowArchived((shown) => !shown)}
                                className="rounded-md px-2 py-1 text-body-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                            >
                                {showArchived ? 'Hide archived' : `Show archived (${archivedCount})`}
                            </button>
                        )}
                        <GTButton styleType="icon" onClick={openFlow} icon={icons.plus} tooltipText="New goal" />
                    </div>
                )}
                <GoalsList
                    selectedGoalId={selectedGoalId}
                    onSelect={toggleSelection}
                    hideSectionHeader
                    showArchived={showArchived}
                    reviewLines={reviewLines}
                />
            </ScrollableListTemplate>
            {/* Desktop keeps the details column open; mobile mounts it only for a detail route. */}
            {showDetailsPane &&
                (!isMobile || goalId || isReview) &&
                (isReview ? (
                    // The summary needs no goal; a step waits for the one its URL names.
                    isReviewReady && review && hasQueue && (selectedGoal || (!goalId && !nextUnread)) ? (
                        <GoalReviewPane goal={selectedGoal ?? null} review={review} />
                    ) : (
                        <Spinner />
                    )
                ) : selectedGoal ? (
                    <GoalDetails goal={selectedGoal} />
                ) : shouldShowMobileDetailSpinner ? (
                    <Spinner />
                ) : (
                    <EmptyDetails
                        icon={icons.check_circle_wavy}
                        text={allGoals.length > 0 ? 'Select a goal to see its details' : 'You have no goals yet'}
                    />
                ))}
        </>
    )
}

export default GoalsView
